import React, { useState, useEffect, useMemo } from 'react';
import { X, ScanLine, Keyboard, CheckCircle2, Circle, Package, AlertCircle, Search } from 'lucide-react';
import axios from 'axios';
import QrScanner from 'react-qr-scanner';

/**
 * Modal for scanning packages during pickup
 * Shows all packages from customer (or provider stores) for today + future
 * Allows QR scanning, manual entry, bulk selection, and real-time search
 */
function PickupPackageScannerModal({ isOpen, onClose, orderId, driverId }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Scan mode states
  const [scanMode, setScanMode] = useState(null); // null | 'qr' | 'manual'
  const [manualId, setManualId] = useState('');
  const [backCameraId, setBackCameraId] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);

  // Fetch packages when modal opens
  useEffect(() => {
    if (isOpen && orderId) {
      fetchPackages();
    }
  }, [isOpen, orderId]);

  // Detect back camera
  useEffect(() => {
    if (!isOpen || scanMode !== 'qr') return;
    
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const rear = devices.find(d =>
          d.kind === 'videoinput' &&
          /rear|back|environment/i.test(d.label)
        );
        setBackCameraId(rear?.deviceId || null);
      })
      .catch(err => console.error('Camera enumeration failed', err));
  }, [isOpen, scanMode]);

  // Auto-hide message
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setScanMode(null);
      setManualId('');
      setSelectedIds(new Set());
      setSearchTerm('');
      setMessage(null);
    }
  }, [isOpen]);

  /**
   * Fetch packages for pickup
   */
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/orders/${orderId}/pickup-packages`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setPackages(response.data.packages || []);
      setProviderInfo(response.data.provider_info);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cargar paquetes'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Filter packages based on search term
   * Searches: delivery_point_id, recipient_name, recipient_phone, product, district, tracking_code, store_name
   */
  const filteredPackages = useMemo(() => {
    if (!searchTerm.trim()) {
      return packages;
    }

    const search = searchTerm.toLowerCase().trim();

    return packages.filter(pkg => {
      // Search in delivery_point_id (convert to string)
      if (pkg.delivery_point_id?.toString().includes(search)) return true;

      // Search in recipient_name
      if (pkg.recipient_name?.toLowerCase().includes(search)) return true;

      // Search in recipient_phone
      if (pkg.recipient_phone?.toLowerCase().includes(search)) return true;

      // Search in product
      if (pkg.product?.toLowerCase().includes(search)) return true;

      // Search in district
      if (pkg.district?.toLowerCase().includes(search)) return true;

      // Search in tracking_code
      if (pkg.tracking_code?.toLowerCase().includes(search)) return true;

      // Search in store_name
      if (pkg.store_name?.toLowerCase().includes(search)) return true;

      return false;
    });
  }, [packages, searchTerm]);

  /**
   * Scan single package (QR or manual)
   */
  const scanPackage = async (identifier, isTrackingCode = false) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        driver_id: driverId,
        new_status: 'with_driver'
      };

      if (isTrackingCode) {
        payload.tracking_code = identifier;
      } else {
        payload.delivery_point_id = parseInt(identifier);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/packages/scan-pickup`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { delivery_point } = response.data;

      setMessage({
        type: 'success',
        text: `✓ ${delivery_point.recipient_name} - ${delivery_point.product}`
      });

      // Refresh packages to update status
      await fetchPackages();

      // Close scan mode
      setScanMode(null);
      setManualId('');

    } catch (error) {
      console.error('Error scanning package:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al escanear paquete'
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle QR scan
   */
  const handleQrScan = (result) => {
    if (!result || submitting) return;
    const code = typeof result === 'string' ? result : result?.text;
    if (!code) return;
    
    // Assume QR contains tracking_code
    scanPackage(code, true);
  };

  /**
   * Handle manual entry
   */
  const handleManualSubmit = () => {
    if (!manualId.trim()) {
      setMessage({ type: 'error', text: 'Ingrese un ID válido' });
      return;
    }
    // Manual entry is delivery_point_id
    scanPackage(manualId.trim(), false);
  };

  /**
   * Toggle package selection for bulk
   */
  const toggleSelection = (deliveryPointId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deliveryPointId)) {
        newSet.delete(deliveryPointId);
      } else {
        newSet.add(deliveryPointId);
      }
      return newSet;
    });
  };

  /**
   * Select/deselect all (only unscanned packages in filtered list)
   */
  const toggleSelectAll = () => {
    const unscannedPackages = filteredPackages.filter(p => p.status !== 'with_driver');
    const unscannedIds = unscannedPackages.map(p => p.delivery_point_id);
    
    // Check if all visible unscanned packages are selected
    const allVisibleSelected = unscannedIds.every(id => selectedIds.has(id));
    
    if (allVisibleSelected) {
      // Deselect only the visible ones
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        unscannedIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all visible unscanned packages
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        unscannedIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  /**
   * Submit bulk selection
   */
  const handleBulkSubmit = async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'error', text: 'Seleccione al menos un paquete' });
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/packages/bulk-pickup`,
        {
          delivery_point_ids: Array.from(selectedIds),
          driver_id: driverId,
          new_status: 'with_driver'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setMessage({
        type: 'success',
        text: response.data.message
      });

      // Refresh packages
      await fetchPackages();
      setSelectedIds(new Set());

    } catch (error) {
      console.error('Error bulk scanning:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al escanear paquetes'
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Clear search
   */
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  if (!isOpen) return null;

  // Count scanned vs total
  const scannedCount = packages.filter(p => p.status === 'with_driver').length;
  const totalCount = packages.length;
  const unscannedPackages = filteredPackages.filter(p => p.status !== 'with_driver');
  const filteredCount = filteredPackages.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-montserrat font-bold text-gray-800">
              Escanear Paquetes
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {scannedCount}/{totalCount} escaneados
            </p>
            {providerInfo?.has_provider && (
              <p className="text-xs text-gray-400 mt-1">
                {providerInfo.total_stores} tienda(s) en este proveedor
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Message Banner */}
        {message && (
          <div className={`mx-4 mt-4 p-3 rounded-lg border-l-4 flex-shrink-0 ${
            message.type === 'success'
              ? 'bg-green-50 border-brand-green text-brand-green'
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p className="font-montserrat font-semibold text-sm">
                {message.text}
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : scanMode === null ? (
          /* Main View - Package List + Action Buttons */
          <>
            {/* Action Buttons */}
            <div className="px-4 pt-4 flex-shrink-0 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setScanMode('qr')}
                  disabled={submitting}
                  className="bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <ScanLine className="w-5 h-5" />
                  <span>Escanear QR</span>
                </button>

                <button
                  onClick={() => setScanMode('manual')}
                  disabled={submitting}
                  className="bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Keyboard className="w-5 h-5" />
                  <span>Ingresar ID</span>
                </button>
              </div>

              {unscannedPackages.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  disabled={submitting}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-montserrat font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {unscannedPackages.every(p => selectedIds.has(p.delivery_point_id)) ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-brand-primary" />
                      <span>Deseleccionar Todos</span>
                    </>
                  ) : (
                    <>
                      <Circle className="w-5 h-5" />
                      <span>Seleccionar Todos</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Search Bar */}
            <div className="px-4 pt-3 pb-2 flex-shrink-0">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por ID, nombre, teléfono, producto..."
                  className="w-full pl-10 pr-10 py-2.5 border-2 border-gray-200 rounded-lg focus:border-brand-primary focus:outline-none font-montserrat text-sm transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  </button>
                )}
              </div>
              
              {/* Search Results Count */}
              {searchTerm && (
                <p className="text-xs text-gray-500 mt-2">
                  {filteredCount === 0 ? (
                    'No se encontraron coincidencias'
                  ) : (
                    `${filteredCount} coincidencia${filteredCount !== 1 ? 's' : ''} de ${totalCount} paquetes`
                  )}
                </p>
              )}
            </div>

            {/* Package List */}
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {filteredPackages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="font-montserrat font-semibold mb-2">
                    {searchTerm ? 'No se encontraron paquetes' : 'No hay paquetes para recoger'}
                  </p>
                  {searchTerm && (
                    <p className="text-sm text-gray-400">
                      Intente con otro término de búsqueda
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredPackages.map((pkg) => {
                    const isSelected = selectedIds.has(pkg.delivery_point_id);
                    const isScanned = pkg.status === 'with_driver';
                    
                    return (
                      <button
                        key={pkg.delivery_point_id}
                        onClick={() => !isScanned && toggleSelection(pkg.delivery_point_id)}
                        disabled={submitting || isScanned}
                        className={`
                          w-full text-left p-4 rounded-lg border-2 transition-all
                          ${isScanned
                            ? 'border-brand-green bg-brand-green/5 opacity-70'
                            : isSelected
                            ? 'border-brand-primary bg-brand-primary/5'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                          }
                          ${submitting || isScanned ? 'cursor-not-allowed' : ''}
                        `}
                      >
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className="flex-shrink-0 mt-1">
                            {isScanned ? (
                              <CheckCircle2 className="w-6 h-6 text-brand-green" />
                            ) : isSelected ? (
                              <CheckCircle2 className="w-6 h-6 text-brand-primary" />
                            ) : (
                              <Circle className="w-6 h-6 text-gray-400" />
                            )}
                          </div>

                          {/* Package Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`
                                w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                                ${isScanned
                                  ? 'bg-brand-green text-white'
                                  : isSelected
                                  ? 'bg-brand-primary text-white'
                                  : 'bg-gray-200 text-gray-600'
                                }
                              `}>
                                {pkg.sequence}
                              </span>
                              <span className="font-semibold text-gray-900 truncate">
                                {pkg.recipient_name}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 truncate mb-1">
                              {pkg.product}
                            </p>

                            {pkg.store_name && (
                              <p className="text-xs text-gray-500 mb-1">
                                Tienda: {pkg.store_name}
                              </p>
                            )}
                            
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">
                                ID: {pkg.delivery_point_id}
                              </span>
                              {pkg.tracking_code && (
                                <span className="text-xs text-gray-500">
                                  • {pkg.tracking_code}
                                </span>
                              )}
                              {pkg.district && (
                                <span className="text-xs text-gray-500">
                                  • {pkg.district}
                                </span>
                              )}
                              {pkg.recipient_phone && (
                                <span className="text-xs text-gray-500">
                                  • {pkg.recipient_phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bulk Submit Button */}
            {selectedIds.size > 0 && (
              <div className="p-4 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleBulkSubmit}
                  disabled={submitting}
                  className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold py-4 px-6 rounded-lg transition-all disabled:opacity-50"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Procesando...</span>
                    </div>
                  ) : (
                    `Escanear ${selectedIds.size} Paquete${selectedIds.size !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            )}
          </>
        ) : scanMode === 'qr' ? (
          /* QR Scanner View */
          <div className="flex-1 flex flex-col p-6">
            <div className="relative bg-black rounded-lg overflow-hidden mb-4">
              <QrScanner
                delay={300}
                onError={e => console.error('QR Scanner error:', e)}
                onScan={handleQrScan}
                style={{ width: '100%' }}
                constraints={{
                  video: backCameraId
                    ? { deviceId: { exact: backCameraId } }
                    : { facingMode: { ideal: 'environment' } }
                }}
              />
            </div>
            <p className="text-center text-sm text-gray-600 mb-4">
              Apunte la cámara al código QR del paquete
            </p>
            <button
              onClick={() => setScanMode(null)}
              disabled={submitting}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        ) : (
          /* Manual Entry View */
          <div className="flex-1 flex flex-col p-6">
            {/* Display Area */}
            <div className="bg-gray-100 border-2 border-brand-primary rounded-lg p-6 min-h-[80px] flex items-center justify-center mb-4">
              <div className="text-center w-full">
                {manualId ? (
                  <p className="font-montserrat font-bold text-3xl text-gray-800 tracking-wider">
                    {manualId}
                  </p>
                ) : (
                  <p className="font-montserrat text-gray-400 text-lg">
                    Ingrese el ID
                  </p>
                )}
              </div>
            </div>

            {/* Custom Numeric Keypad */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button
                  key={num}
                  onClick={() => setManualId(prev => prev + num.toString())}
                  disabled={submitting}
                  className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary disabled:opacity-50"
                >
                  {num}
                </button>
              ))}
              
              <button
                onClick={() => setManualId('')}
                disabled={submitting}
                className="bg-red-100 hover:bg-red-200 active:bg-red-300 text-red-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-red-200 disabled:opacity-50"
              >
                Borrar
              </button>
              
              <button
                onClick={() => setManualId(prev => prev + '0')}
                disabled={submitting}
                className="bg-gray-100 hover:bg-brand-primary/10 active:bg-brand-primary/20 text-gray-800 font-montserrat font-bold text-2xl py-6 rounded-lg transition-colors border-2 border-gray-200 hover:border-brand-primary disabled:opacity-50"
              >
                0
              </button>
              
              <button
                onClick={() => setManualId(prev => prev.slice(0, -1))}
                disabled={submitting}
                className="bg-orange-100 hover:bg-orange-200 active:bg-orange-300 text-orange-700 font-montserrat font-semibold text-lg py-6 rounded-lg transition-colors border-2 border-orange-200 disabled:opacity-50"
              >
                ←
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleManualSubmit}
                disabled={!manualId || submitting}
                className={`flex-1 font-montserrat font-semibold py-4 px-6 rounded-lg transition-all ${
                  manualId && !submitting
                    ? 'bg-brand-primary hover:bg-brand-primary/90 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Procesando...' : 'Confirmar'}
              </button>
              <button
                onClick={() => {
                  setScanMode(null);
                  setManualId('');
                }}
                disabled={submitting}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-montserrat font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PickupPackageScannerModal;